import React, { useEffect, useState, useMemo } from 'react';
import { useParams, Redirect } from 'react-router-dom';
import { useIntl } from 'react-intl';
import isEmpty from 'lodash/isEmpty';
import { useDispatch } from 'react-redux';
import Loading from 'components/common/Loading';
import Metadata from 'components/common/Metadata';
import EventVideoPlayer from 'components/Event/eventVideoPlayer';
import { EventContextProvider } from 'contexts/EventContext';
import UnlockEventLanding from 'components/unlock/UnlockEvent';
import NotFoundPage from 'pages/NotFoundPage';
import { useCelebrity, useToast, useAnalytics, useSession, useModal } from 'hooks';
import { checkSubscription, getFullName } from 'utils/helpers';
import { MIXPANEL_EVENTS } from 'constants/constants';
import useProfile from 'hooks/users/useProfile';
import { getCelebrity } from 'state/actions/celebritiesActions';
import { routesPaths } from 'constants/routesPaths';
import AuthModal from 'components/user/AuthModal/AuthModal';
import Modal from 'components/common/Modal/Modal';
import { zIndexModalSecond } from 'styles/common/_constants.scss';

const EventPage = () => {
  const intl = useIntl();
  const { showErrorToast } = useToast();
  const { trackEvent } = useAnalytics();
  const { eventId, celebrityId } = useParams();
  const { authenticated } = useSession();
  const dispatch = useDispatch();
  const { toggle: toggleAuth, isShowing: isShowingAuth } = useModal();
  const [authSuccessCallback, setAuthSuccessCallback] = useState(null);
  const [isLoadingAfterAuth, setIsLoadingAfterAuth] = useState(false);
  const [authModalShown, setAuthModalShown] = useState(false);

  const {
    requestProfile,
    loading: profileLoading,
    profile: { memberships },
  } = useProfile();

  const { celebrity, error, loading: celebrityLoading, refresh, status } = useCelebrity();
  const { events } = celebrity;

  const resetCelebrity = celebrity.id !== Number(celebrityId);
  const event = useMemo(() => events?.find(item => item.id == eventId), [events, eventId]);
  const eventUnavailable = !event && !resetCelebrity;

  // Handler for refreshing celebrity data
  const handleReset = () => {
    dispatch(getCelebrity(celebrityId));
  };

  // Handle post-authentication flow
  const handleAuthSuccess = () => {
    console.log("Auth success handler called");
    setIsLoadingAfterAuth(true);
    
    // Load user profile to get membership info
    requestProfile();
    
    // Refresh the celebrity data to get updated event information
    refresh(celebrityId);
  };

  // Show auth modal with an optional callback to execute after login
  const showAuthModal = (callback = null) => {
    if (callback) {
      setAuthSuccessCallback(() => callback);
    }
    setAuthModalShown(true);
    toggleAuth();
  };

  // Is the event free?
  const IS_FREE_EVENT = useMemo(
    () => event && (
      (event.membersPrice === 0 && 
      event.nonMembersPrice === 0) || 
      (event.membersPrice === "0" && 
      event.nonMembersPrice === "0") ||
      (event.membersPrice === "0.0" && 
      event.nonMembersPrice === "0.0") ||
      (event.membersPrice === 0.0 && 
      event.nonMembersPrice === 0.0)
    ),
    [event]
  );

  // Free event but user not authenticated
  const NEEDS_AUTH_FOR_FREE_EVENT = useMemo(
    () => IS_FREE_EVENT && !authenticated,
    [IS_FREE_EVENT, authenticated]
  );

  // Monitor the loading state after authentication to ensure data is refreshed
  useEffect(() => {
    if (isLoadingAfterAuth && !celebrityLoading && !profileLoading && event) {
      console.log("Data refreshed after auth:", {
        event,
        videoFileUrl: event?.videoFileUrl,
        subscribedToEvent: event?.subscribedToEvent,
        isFreeEvent: IS_FREE_EVENT
      });
      
      // Set loading to false regardless of whether we have videoFileUrl yet
      setIsLoadingAfterAuth(false);
      
      // Execute any stored callback after login and data refresh are complete
      if (authSuccessCallback) {
        authSuccessCallback();
        setAuthSuccessCallback(null);
      }
    }
  }, [isLoadingAfterAuth, celebrityLoading, profileLoading, event, authSuccessCallback, IS_FREE_EVENT]);

  // Show auth modal for free events when user is not authenticated
  useEffect(() => {
    if (NEEDS_AUTH_FOR_FREE_EVENT && !isShowingAuth && !authModalShown) {
      console.log("Showing auth modal for free event");
      setAuthModalShown(true);
      toggleAuth();
    }
  }, [NEEDS_AUTH_FOR_FREE_EVENT, isShowingAuth, authModalShown]);

  // Initial data loading
  useEffect(() => {
    console.log("Initial data loading");
    requestProfile();
    (!celebrity || resetCelebrity) && dispatch(getCelebrity(celebrityId));
  }, []);

  useEffect(() => {
    isEmpty(celebrity) && dispatch(getCelebrity(celebrityId));
  }, [celebrity?.firstName]);

  useEffect(() => {
    if (eventUnavailable) {
      showErrorToast(intl.formatMessage({ id: 'events.eventUnavailable' }));
    }
    if (event && !resetCelebrity) {
      const { firstName, lastName } = celebrity;
      trackEvent(MIXPANEL_EVENTS.eventPlayerView, {
        'Creator Name': getFullName(firstName, lastName),
        'Event Title': event.title,
        id: event.id,
        typeName: 'event'
      });
    }
  }, [event, resetCelebrity, eventUnavailable]);

  const eventPrice = useMemo(() => {
    if (!event) return null;
    return checkSubscription(memberships, celebrity?.id)
      ? event.membersPrice?.toFixed(2)
      : event.nonMembersPrice?.toFixed(2);
  }, [event, memberships, celebrity]);

  // User needs to buy the event
  const NEED_BUY_EVENT = useMemo(
    () => eventPrice !== null && eventPrice !== undefined && !event?.videoFileUrl && !event.subscribedToEvent && !IS_FREE_EVENT,
    [eventPrice, event, IS_FREE_EVENT]
  );

  // Show the video player (when the user has access)
  const SHOW_PLAYER = useMemo(
    () => {
      // Log for debugging
      console.log("SHOW_PLAYER calculation:", {
        videoFileUrl: event?.videoFileUrl,
        subscribedToEvent: event?.subscribedToEvent,
        IS_FREE_EVENT,
        result: event?.videoFileUrl && (event.subscribedToEvent || IS_FREE_EVENT)
      });
      
      // For free events, we always want to show the player if authenticated
      if (IS_FREE_EVENT && authenticated) {
        return true;
      }
      
      // For other cases, check videoFileUrl and subscribedToEvent
      return event?.videoFileUrl && (event.subscribedToEvent || IS_FREE_EVENT);
    },
    [event, IS_FREE_EVENT, authenticated]
  );

  // Show unlock screen for purchasing
  const SHOW_UNLOCK_CONTENT = useMemo(
    () => NEED_BUY_EVENT,
    [NEED_BUY_EVENT]
  );

  const UNLOCK_CONFIG = useMemo(() => {
    if (NEED_BUY_EVENT) {
      return {
        title: intl.formatMessage({ id: 'unlock.title' }),
        buttonLabel: intl.formatMessage({ id: 'unlock.event' }),
        price: eventPrice,
        episodeName: event?.title,
        purchaseType: 'EVENT',
      };
    }
    return {};
  }, [NEED_BUY_EVENT, intl, eventPrice, event]);

  // Determine when to show loading state
  const shouldShowLoading = useMemo(() => {
    console.log("shouldShowLoading calculation:", {
      isEmpty: isEmpty(events),
      resetCelebrity,
      profileLoading,
      celebrityLoading,
      isLoadingAfterAuth,
      NEEDS_AUTH_FOR_FREE_EVENT,
      isShowingAuth,
      authenticated
    });
    
    // Basic loading conditions
    const initialLoading = isEmpty(events) || resetCelebrity || profileLoading || celebrityLoading;
    
    // Show loading during authentication and data refresh, but not if showing auth modal
    if (isLoadingAfterAuth) {
      return true;
    }
    
    // Don't show loading when showing auth modal
    if (isShowingAuth) {
      return false;
    }
    
    // For free events, if we're authenticated but don't have the video URL yet, keep loading
    if (IS_FREE_EVENT && authenticated && !event?.videoFileUrl && !isLoadingAfterAuth) {
      // If we're authenticated for a free event but don't have the URL, refresh the data
      if (!isLoadingAfterAuth) {
        console.log("Triggering refresh for free event after auth");
        setIsLoadingAfterAuth(true);
        refresh(celebrityId);
      }
      return true;
    }
    
    return initialLoading;
  }, [
    events, 
    resetCelebrity, 
    profileLoading, 
    celebrityLoading, 
    isLoadingAfterAuth, 
    NEEDS_AUTH_FOR_FREE_EVENT, 
    isShowingAuth, 
    authenticated, 
    IS_FREE_EVENT, 
    event?.videoFileUrl, 
    refresh, 
    celebrityId
  ]);

  if (error) return <NotFoundPage />;

  // If we're showing the auth modal, don't show loading
  if (shouldShowLoading && !isShowingAuth) {
    return (
      <div className="home-loading">
        <Loading />
      </div>
    );
  }

  if (eventUnavailable) {
    return <Redirect to={routesPaths.home} />;
  }
  
  return (
    <EventContextProvider>
      <Metadata socialMediaMetadata={UNLOCK_CONFIG} />
      {SHOW_UNLOCK_CONTENT && (
        <UnlockEventLanding 
          event={event} 
          celebrity={celebrity} 
          config={UNLOCK_CONFIG} 
          onSuccess={handleReset}
          showAuthModal={showAuthModal}
        />
      )}
      {SHOW_PLAYER && (
        <EventVideoPlayer 
          authenticated={authenticated} 
          event={event} 
          celebrity={celebrity} 
        />
      )}
      <Modal 
        hide={() => {
          toggleAuth();
          // If user closes the auth modal for a free event, redirect to home
          if (NEEDS_AUTH_FOR_FREE_EVENT) {
            window.location.href = routesPaths.home;
          }
        }} 
        isShowing={isShowingAuth} 
        zIndex={zIndexModalSecond}
      >
        <AuthModal isModal onSuccess={handleAuthSuccess} />
      </Modal>
    </EventContextProvider>
  );
};

export default EventPage;
